import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { BayclubsAlertService } from '@bayclubs/alert';
import { api, ConfirmationService, ErrorTranslationService, ModalService } from '@bayclubs/utils';

import { RefundService } from '@bayclubs/payments-processor-office-api';
import { PaymentProcessorCloseReason, PaymentProcessorOfficeLauncherService, PaymentsProcessorOfficeLauncherModel } from '@bayclubs/payments-processor-office-launcher';
import { catchError, filter, of, repeat, skipWhile, Subscription, switchMap, take, tap } from 'rxjs';
import { InfoService } from '../../../../infrastructure/infrastructure.module';
import { campsenum } from '../../../models/enums.generated';
import { camps } from '../../../models/models.generated';
import { PurchaseApiService } from '../../../services/api/purchase-api.service';
import { PaymentProcessorIntegrationService } from '../../../services/payment-processor-integration.service';
import { PaymentsProcessorLaunchAuthService } from '../../../services/payments-processor-launch-auth.service';
import { PurchaseEditRefundReasonModalComponent } from '../purchase-edit/purchase-edit-refund-reason-modal/purchase-edit-refund-reason-modal.component';

@Component({
    selector: 'app-purchase-details',
    templateUrl: './purchase-details.component.html',
    styleUrls: ['./purchase-details.component.scss'],
    standalone: false,
})
export class PurchaseDetailsComponent implements OnInit, OnDestroy {
    public purchase: camps.CampPurchaseDetails;
    private subscription = new Subscription();
    checkStatusSubscription: Subscription = new Subscription();
    waitingForPaymentProcessorConfirmation = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private translate: TranslateService,
        private confirmation: ConfirmationService,
        private api: PurchaseApiService,
        private info: InfoService,
        private paymentProcessorRefundService: RefundService,
        private alert: BayclubsAlertService,
        private errorTranslate: ErrorTranslationService,
        private router: Router,
        private modal: ModalService,
        private paymentProcessorIntegrationService: PaymentProcessorIntegrationService,
        private refundService: RefundService,
        private launchAuth: PaymentsProcessorLaunchAuthService,
        private paymentProcessorOfficeLauncherService: PaymentProcessorOfficeLauncherService
    ) { }

    ngOnDestroy(): void {
        this.checkStatusSubscription.unsubscribe();
        this.subscription.unsubscribe();
    }

    ngOnInit() {
        this.activatedRoute.params.subscribe((params) => {
            if (params.id) {
                this.loadDetails(params.id);
            }
        });
    }

    public fullRefund(): void {
        if (this.purchase.isPaymentProcessorPurchase) {
            this.performPaymentProcessorRefund();
        } else {
            this.refund();
        }
    }

    public refund(): void {
        this.subscription.add(
            this.modal
                .create(PurchaseEditRefundReasonModalComponent, {
                    purchase: this.purchase,
                })
                .pipe(
                    take(1),
                    filter((refundReasonResult: PurchaseEditRefundReasonModalComponent) => !!refundReasonResult?.value),
                    switchMap((refundReasonResult) => {
                        return this.api.fullRefund(this.purchase.purchaseId, {
                            purchaseSetId: this.purchase.purchaseSetId,
                            refundReason: refundReasonResult.value.reason,
                            comment: refundReasonResult.value.comment,
                        } as camps.SetRefundReasonRequest);
                    }),
                    switchMap((response: api.OperationResult) => {
                        if (response.isSuccess) {
                            this.alert.showInfo(
                                this.translate.instant(
                                    !this.isPaymentProcessorPurchase ? 'PurchaseDetails.purchaseCanceledDialogTitle' : 'PurchaseDetails.purchaseByPaymentProcessorCanceledDialogTitle'
                                )
                            );
                            console.log('refunded successfully');
                        } else {
                            this.errorTranslate.translateResponse(response);
                            this.info.show(this.translate.instant('PurchaseDetails.purchaseCancelFailedDialogTitle'), response.validationNotification.errors[0].message);
                            console.error('refund failed ' + (response.errorMessage || response.validationNotification.errors[0].message));
                        }

                        this.loadDetails(this.purchase.purchaseId);
                        return of();
                    })
                )
                .subscribe()
        );
    }

    private performPaymentProcessorRefund() {
        this.subscription.add(
            this.modal
                .create(PurchaseEditRefundReasonModalComponent, {
                    purchase: this.purchase,
                })
                .pipe(
                    take(1),
                    filter((refundReasonResult: PurchaseEditRefundReasonModalComponent) => !!refundReasonResult?.value),
                    switchMap((refundReasonResult) =>
                        this.api.initRefund(this.purchase.purchaseId, {
                            purchaseSetId: this.purchase.purchaseSetId,
                            refundReason: refundReasonResult.value.reason,
                            comment: refundReasonResult.value.comment,
                        } as camps.SetRefundReasonRequest)
                    ),
                    switchMap((initRefundResult) => {
                        if (!initRefundResult.isSuccess) {
                            this.errorTranslate.translateValidationNotifications(initRefundResult.validationNotification);
                            this.alert.showError(initRefundResult.validationNotification.errors[0]?.message ?? initRefundResult.errorMessage);
                            return of();
                        }

                        const request = this.paymentProcessorIntegrationService.mapToPaymentProcessorFullRefund(this.purchase, initRefundResult.value);
                        this.waitingForPaymentProcessorConfirmation = true;
                        return this.refundService.api10RefundsPost(request);
                    }),
                    switchMap((response) => {
                        const ppRefundRequest: PaymentsProcessorOfficeLauncherModel = {
                            accessToken: this.launchAuth.accessToken,
                            authMode: this.launchAuth.authMode,
                            identityHintProvider: this.launchAuth.identityHintProvider,
                            refundId: response.refundScheduleId,
                            cancelOnModalClose: true,
                            correlationId: response.correlationId,
                            officeAppUrl: response.officeAppUrl,
                            parentOrigin: window.location.origin,
                            theme: 'dark',
                            transactionId: null,
                        };
                        return this.paymentProcessorOfficeLauncherService.open(ppRefundRequest).pipe(
                            switchMap((paymentsProcessorResult) => {
                                if (paymentsProcessorResult.closeReason === PaymentProcessorCloseReason.Success) {
                                    this.checkRefundStatus(this.purchase.purchaseId);
                                    return of();
                                } else {
                                    this.waitingForPaymentProcessorConfirmation = false;
                                    return of();
                                }
                            })
                        );
                    }),
                    catchError((error) => {
                        this.waitingForPaymentProcessorConfirmation = false;
                        this.alert.showError(error?.error?.Errors[0]?.Message ?? error.message);
                        return of();
                    })
                )
                .subscribe()
        );
    }

    public get isRefunded(): boolean {
        if (!this.purchase) return false;

        return campsenum.CampPurchaseStatus[this.purchase.status] == campsenum.CampPurchaseStatus.Refunded;
    }

    public get isPaymentProcessorPurchase(): boolean {
        return this.purchase?.isPaymentProcessorPurchase === true;
    }

    public get isPartialRefundPossible(): boolean {
        if (!this.purchase) return false;

        if (this.purchase.campType === campsenum.CampType.Childcare) return false;

        return !this.purchase.couponDetails || !this.purchase.couponDetails.coupon;
    }

    public onEdit(): void {
        this.router.navigate([`/purchases/${this.purchase.purchaseSetId}/edit`]);
    }

    goBack() {
        this.router.navigate(['/purchases']);
    }

    private loadDetails(id: camps.Guid): void {
        this.api.get(id).subscribe(
            (response: api.OperationResultValue<camps.CampPurchaseDetails>) => {
                this.purchase = response.value;
            },
            (error) => {
                console.log(error);
            },
            () => { }
        );
    }

    checkRefundStatus(purchaseId: camps.Guid) {
        this.waitingForPaymentProcessorConfirmation = true;
        const expectedStatus = campsenum.CampPurchaseStatus[campsenum.CampPurchaseStatus.Refunded];

        this.checkStatusSubscription.add(
            this.api
                .get(purchaseId)
                .pipe(
                    repeat({ delay: 2000 }),
                    tap((response) => console.log(response)),
                    skipWhile((response) => response.value.status !== expectedStatus),
                    take(1)
                )
                .subscribe((response) => {
                    this.purchase = response.value;
                    this.waitingForPaymentProcessorConfirmation = false;
                })
        );
    }
}
